import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useCreateSupplier, useUpdateSupplier } from '@hooks/useProcurement'

const supplierSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters'),
  gstin: z.string().optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
})

type SupplierFormData = z.infer<typeof supplierSchema>

interface SupplierFormProps {
  initialData?: any
  onSuccess?: () => void
  onCancel?: () => void
}

export function SupplierForm({
  initialData,
  onSuccess,
  onCancel,
}: SupplierFormProps) {
  const [error, setError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SupplierFormData>({
    defaultValues: initialData || {
      name: '',
      gstin: '',
      phone: '',
      address: '',
    },
  })

  const create = useCreateSupplier()
  const update = useUpdateSupplier(initialData?.id)

  const onSubmit = async (data: SupplierFormData) => {
    try {
      setError(null)
      const cleanData = {
        ...data,
        gstin: data.gstin || null,
        phone: data.phone || null,
        address: data.address || null,
      }

      if (initialData?.id) {
        await update.mutateAsync(cleanData)
      } else {
        await create.mutateAsync(cleanData)
      }

      onSuccess?.()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save supplier')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Supplier Name *
        </label>
        <input
          {...register('name')}
          type="text"
          placeholder="e.g., Selvam Traders"
          className="w-full"
        />
        {errors.name && (
          <span className="text-red-500 text-sm">{errors.name.message}</span>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          GSTIN
        </label>
        <input
          {...register('gstin')}
          type="text"
          placeholder="e.g., 33AABCT1234A1Z5"
          className="w-full"
        />
        {errors.gstin && (
          <span className="text-red-500 text-sm">{errors.gstin.message}</span>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Phone
        </label>
        <input
          {...register('phone')}
          type="tel"
          placeholder="10-digit mobile number"
          className="w-full"
        />
        {errors.phone && (
          <span className="text-red-500 text-sm">{errors.phone.message}</span>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address
        </label>
        <textarea
          {...register('address')}
          placeholder="Full address"
          rows={3}
          className="w-full"
        />
        {errors.address && (
          <span className="text-red-500 text-sm">{errors.address.message}</span>
        )}
      </div>

      <div className="flex gap-2 justify-end pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Supplier'}
        </button>
      </div>
    </form>
  )
}
